import React, { useContext, useEffect, useState } from 'react';
import {
    Modal, ModalHeader, ModalBody, Form, FormGroup, Label, Input, Button, Row, Col, Table
} from 'reactstrap';
import InputMask from 'react-input-mask';
import PaginationMaterial from '../../components/PaginationMaterial/PaginationMaterial';

import { InstituicaoContext } from '../../context/InstituicaoContext';
import { ResponsavelContext } from '../../context/ResponsavelContext';
import { AuthContext } from '../../context/AuthContext';
import useUser from '../../context/hooks/useUser';

import { EditInstituicaoType } from '../../context/hooks/useInstituicao';
import { ResponsavelType, EditResponsavelType } from '../../context/hooks/useResponsavel';

import { CreateResponsavelModal } from '../Responsavel/CreateResponsavelModal';
import { EditResponsavelModal } from '../Responsavel/EditResponsavelModal';

interface Props {
    isOpen: boolean;
    toggle: () => void;
    instituicaoData: EditInstituicaoType;
    reloadList: () => void;
}

export const EditInstituicaoModal: React.FC<Props> = ({
    isOpen,
    toggle,
    instituicaoData,
    reloadList
}) => {
    const { objUserTk } = useContext(AuthContext);
    const { updateInstituicao } = useContext(InstituicaoContext);
    const {
        getResponsavelPaged,
        deleteResponsavel,
        updateResponsavel,
        createResponsavel,
        listResponsavel,
        responsavelPageCount,
    } = useContext(ResponsavelContext);
    const { getPermissionsByUser, userPermissions } = useUser();

    const [formData, setFormData] = useState<EditInstituicaoType>(instituicaoData);
    const [editingResponsavel, setEditingResponsavel] = useState<EditResponsavelType | null>(null);
    const [responsavelPage, setResponsavelPage] = useState(1);
    const [createResponsavelModalOpen, setCreateResponsavelModalOpen] = useState(false);
    const [editResponsavelModalOpen, setEditResponsavelModalOpen] = useState(false);

    useEffect(() => {
        if (isOpen && instituicaoData.id) {
            setFormData(instituicaoData);
            getResponsavelPaged(1, "", objUserTk?.EmpresaId, instituicaoData.id, 9);
        }
    }, [isOpen]);

    useEffect(() => {
        const fetchPermissions = async () => {
            if (objUserTk?.UserId) {
                const permissions = await getPermissionsByUser(objUserTk.UserId);
            }
        };
        
        fetchPermissions().catch(error => console.error("Erro ao carregar permissões:", error));
    }, [objUserTk?.UserId]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const success = await updateInstituicao(formData);
        if (success) {
            reloadList();
            toggle();
        } else {
            console.error("Erro ao atualizar instituição.");
        }
    };

    const onChangePaginationResponsavel = async (e: any, value: number) => {
        setResponsavelPage(value);
        await getResponsavelPaged(value, "", objUserTk?.EmpresaId, formData.id, 9);
    };

    const handleDeleteResponsavel = async (responsavelId: number) => {
        if (window.confirm("Tem certeza que deseja excluir este responsável?")) {
            await deleteResponsavel(responsavelId);
            getResponsavelPaged(responsavelPage, "", objUserTk?.EmpresaId, formData.id, 9);
        }
    };

    const handleEditResponsavelInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setEditingResponsavel(prevResponsavel => ({
            ...prevResponsavel!,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleEditResponsavel = (responsavel: ResponsavelType) => {
        setEditingResponsavel({
            id: responsavel.id,
            name: responsavel.name,
            email: responsavel.email,
            CPF: responsavel.cpf,
            telephone: responsavel.telephone,
            position: responsavel.position,
            bank: responsavel.bank,
            agency: responsavel.agency,
            currentAccount: responsavel.currentAccount,
            observation: responsavel.observation,
            pix: responsavel.pix,
        });
        setEditResponsavelModalOpen(true);
    };

    const handleEditResponsavelSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (editingResponsavel) {
            await updateResponsavel(editingResponsavel);
            setEditResponsavelModalOpen(false);
            getResponsavelPaged(responsavelPage, "", objUserTk?.EmpresaId, formData.id, 9);
        }
    };

    return (
        <>
            <Modal isOpen={isOpen} toggle={toggle} backdrop="static" size="lg">
                <ModalHeader toggle={toggle}>Alterar informações da instituição</ModalHeader>
                <ModalBody>
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col md="6">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="name">Nome</Label>
                                    <Input className="form-control-alternative" type="text" name="name" id="name" value={formData.name} onChange={handleInputChange} required />
                                </FormGroup>
                            </Col>
                            <Col md="6">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="email">Email</Label>
                                    <Input className="form-control-alternative" type="email" name="email" id="email" value={formData.email} onChange={handleInputChange} required />
                                </FormGroup>
                            </Col>
                        </Row>

                        {/* Endereço */}
                        <Row>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="CEP">CEP</Label>
                                    <InputMask className="form-control form-control-alternative" mask="99999-999" name="CEP" id="CEP" value={formData.CEP || ''} onChange={handleInputChange} />
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="state">Estado</Label>
                                    <Input className="form-control-alternative" type="text" name="state" id="state" value={formData.state} onChange={handleInputChange} />
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="city">Cidade</Label>
                                    <Input className="form-control-alternative" type="text" name="city" id="city" value={formData.city} onChange={handleInputChange} />
                                </FormGroup>
                            </Col>
                        </Row>

                        {/* Complemento */}
                        <Row>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="neighborhood">Bairro</Label>
                                    <Input className="form-control-alternative" type="text" name="neighborhood" id="neighborhood" value={formData.neighborhood} onChange={handleInputChange} />
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="address">Endereço</Label>
                                    <Input className="form-control-alternative" type="text" name="address" id="address" value={formData.address} onChange={handleInputChange} />
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="number">Número</Label>
                                    <Input className="form-control-alternative" type="number" name="number" id="number" value={formData.number} onChange={handleInputChange} />
                                </FormGroup>
                            </Col>
                        </Row>

                        {/* Extras */}
                        <Row>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="directorPhone">Telefone do diretor</Label>
                                    <InputMask className="form-control form-control-alternative" mask="(99) 99999-9999" name="directorPhone" id="directorPhone" value={formData.directorPhone || ''} onChange={handleInputChange} />
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="pix">Chave PIX</Label>
                                    <Input className="form-control-alternative" type="text" name="pix" id="pix" value={formData.pix} onChange={handleInputChange} />
                                </FormGroup>
                            </Col>
                            <Col md="4" className="form-check mt-4">
                                <Label className="form-control-label" htmlFor="isPrivate">
                                    <Input className="form-check-input" type="checkbox" name="isPrivate" id="isPrivate" checked={formData.isPrivate} onChange={handleInputChange} />{' '}
                                    Instituição privada?
                                </Label>
                            </Col>
                        </Row>

                        {/* Responsáveis */}
                        {userPermissions.includes(2) && (
                            <Row className="mt-4">
                                <Col>
                                    {userPermissions.includes(1) && (
                                        <Button
                                            className="mt-3 mb-3"
                                            style={{ color: "#FFF", backgroundColor: "#585858", float: "right" }}
                                            size="m"
                                            onClick={() => setCreateResponsavelModalOpen(true)}
                                        >
                                            Adicionar responsável
                                        </Button>
                                    )}
                                    <Table hover responsive className="table-striped">
                                        <thead>
                                            <tr>
                                                <th>Seq.</th>
                                                <th>Nome</th>
                                                <th>Email</th>
                                                <th>CPF</th>
                                                <th>Telefone</th>
                                                <th>Pix</th>
                                                <th>Ações</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {listResponsavel.map((r: ResponsavelType) => {
                                                const phone = (r.telephone ?? "").replace(/\D/g, "");
                                                return (
                                                    <tr key={r.id}>
                                                        <td>{r.id}</td>
                                                        <td>{r.name}</td>
                                                        <td>{r.email || "Não informado"}</td>
                                                        <td>{r.cpf}</td>
                                                        <td><a href={`https://wa.me/${phone}`} target="_blank" rel="noreferrer">{r.telephone}</a></td>
                                                        <td>{r.pix}</td>
                                                        <td>
                                                            {userPermissions.includes(3) && (
                                                                <Button onClick={() => handleEditResponsavel(r)}><i className="fa-solid fa-pen-to-square"></i></Button>
                                                            )}
                                                            {userPermissions.includes(4) && (
                                                                <Button onClick={() => handleDeleteResponsavel(r.id)} color="danger"><i className="fa-solid fa-trash"></i></Button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                    <div className="d-flex justify-content-end mt-3">
                                        {responsavelPageCount > 1 && (
                                            <PaginationMaterial
                                                onChange={onChangePaginationResponsavel}
                                                count={responsavelPageCount}
                                            />
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        )}

                        <div className="d-flex justify-content-end mt-4">
                            <Button style={{ color: "#FFF", backgroundColor: "#585858" }} type="submit">Salvar alterações</Button>
                            <Button style={{ color: "#FFF", backgroundColor: "#d9534f" }} className="ml-2" onClick={toggle}>Cancelar</Button>
                        </div>
                    </Form>
                </ModalBody>
            </Modal>

            <CreateResponsavelModal
                isOpen={createResponsavelModalOpen}
                toggle={() => setCreateResponsavelModalOpen(false)}
                instituicaoId={formData.id}
                onResponsavelCriado={() => getResponsavelPaged(responsavelPage, "", objUserTk?.EmpresaId, formData.id, 9)}
            />

            <EditResponsavelModal
                isOpen={editResponsavelModalOpen}
                toggle={() => setEditResponsavelModalOpen(false)}
                responsavel={editingResponsavel}
                onChange={handleEditResponsavelInputChange}
                onSubmit={handleEditResponsavelSubmit}
            />
        </>
    );
}
export default EditInstituicaoModal;
